import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";
import { filterActiveMasterRows } from "../../memberships/utils/masterLookupUtils";

const MAINTENANCE_BASE = `${config.BASE_URL}/maintenance-control`;

/* =========================
   AUTH HEADERS
========================= */
/* =========================
   RESPONSE PARSER
========================= */
async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function apiErrorMessage(data, fallback) {
  if (!data || typeof data !== "object") return fallback;
  const d = typeof data.details === "string" ? data.details.trim() : "";
  const m = typeof data.message === "string" ? data.message.trim() : "";
  if (d && m) return `${m}: ${d}`;
  return d || m || fallback;
}

/* =========================
   MASTERS (Dropdowns)
========================= */
export async function fetchMaintenanceMasters() {
  const res = await authFetch(`${MAINTENANCE_BASE}/masters`, {
    credentials: "include",
    headers: await buildAuthHeaders()
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to load maintenance masters");

  const raw = data.data || {};
  return {
    ...raw,
    maintenanceTypes: filterActiveMasterRows(raw.maintenanceTypes),
    priorityLevels: filterActiveMasterRows(raw.priorityLevels),
  };
}

/** Active staff search for assign typeahead (maintenance VIEW only). */
export async function searchMaintenanceOfficeUsers(query) {
  const q = String(query ?? "").trim();
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  const res = await authFetch(`${MAINTENANCE_BASE}/office-users/search?${params.toString()}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "User search failed");
  return Array.isArray(data.data) ? data.data : [];
}

/* =========================
   MASTER TABLES (admin UI)
========================= */

function appendMasterListParams(params, filters) {
  params.set("page", String(filters.page ?? 1));
  params.set("limit", String(filters.limit ?? 10));
  if (filters.sortBy) params.set("sortBy", String(filters.sortBy));
  if (filters.sortDirection) params.set("sortDirection", String(filters.sortDirection));
}

export async function fetchMaintenanceTypesMasterList(filters = {}) {
  const params = new URLSearchParams();
  appendMasterListParams(params, filters);
  const res = await authFetch(`${MAINTENANCE_BASE}/master/maintenance-types?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiErrorMessage(data, "Failed to load maintenance types"));
  return data;
}

export async function createMaintenanceTypeMaster(body) {
  const res = await authFetch(`${MAINTENANCE_BASE}/master/maintenance-types`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders(),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiErrorMessage(data, "Failed to create maintenance type"));
  return data;
}

export async function updateMaintenanceTypeMaster(id, body) {
  const res = await authFetch(
    `${MAINTENANCE_BASE}/master/maintenance-types/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: await buildAuthHeaders(),
      body: JSON.stringify(body),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiErrorMessage(data, "Failed to update maintenance type"));
  return data;
}

export async function deleteMaintenanceTypeMaster(id) {
  const res = await authFetch(
    `${MAINTENANCE_BASE}/master/maintenance-types/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: await buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiErrorMessage(data, "Failed to remove maintenance type"));
  return data;
}

export async function fetchMaintenancePrioritiesMasterList(filters = {}) {
  const params = new URLSearchParams();
  appendMasterListParams(params, filters);
  const res = await authFetch(`${MAINTENANCE_BASE}/master/priority-levels?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiErrorMessage(data, "Failed to load priority levels"));
  return data;
}

export async function createMaintenancePriorityMaster(body) {
  const res = await authFetch(`${MAINTENANCE_BASE}/master/priority-levels`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders(),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiErrorMessage(data, "Failed to create priority level"));
  return data;
}

export async function updateMaintenancePriorityMaster(id, body) {
  const res = await authFetch(
    `${MAINTENANCE_BASE}/master/priority-levels/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: await buildAuthHeaders(),
      body: JSON.stringify(body),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiErrorMessage(data, "Failed to update priority level"));
  return data;
}

export async function deleteMaintenancePriorityMaster(id) {
  const res = await authFetch(
    `${MAINTENANCE_BASE}/master/priority-levels/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: await buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiErrorMessage(data, "Failed to remove priority level"));
  return data;
}

/* =========================
   METRICS (Dashboard Cards)
========================= */
export async function fetchMaintenanceMetrics() {
  const res = await authFetch(`${MAINTENANCE_BASE}/metrics`, {
    credentials: "include",
    headers: await buildAuthHeaders()
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to load maintenance metrics");

  return data.data || {};
}

/* =========================
   REQUEST LIST
========================= */
const DEFAULT_PAGE_SIZE = 10;

function appendQueryParam(params, key, value) {
  if (value == null || value === "") return;
  const s = String(value).trim();
  if (s === "") return;
  params.set(key, s);
}

export async function fetchMaintenanceRequests(
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  filters = {}
) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });

  appendQueryParam(params, "name", filters.name);
  appendQueryParam(params, "status_id", filters.status_id);
  appendQueryParam(params, "type_id", filters.type_id);
  appendQueryParam(params, "request_id", filters.request_id);
  appendQueryParam(params, "created_date", filters.created_date);
  appendQueryParam(params, "asset_area", filters.asset_area);
  appendQueryParam(params, "issue_description", filters.issue_description);
  appendQueryParam(params, "reported_by_contains", filters.reported_by_contains);
  appendQueryParam(params, "reported_by_id", filters.reported_by_id);
  appendQueryParam(params, "assigned_name_contains", filters.assigned_name_contains);
  appendQueryParam(params, "cost_match", filters.cost_match);

  const res = await authFetch(`${MAINTENANCE_BASE}/requests?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders()
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to load requests");

  const pag = data.pagination || {};
  return {
    rows: data.data || [],
    total: Number(pag.total ?? data.total ?? 0),
    page: Number(pag.page ?? data.page ?? page),
    pageSize: Number(pag.pageSize ?? data.pageSize ?? pageSize),
    totalPages: Math.max(1, Number(pag.totalPages ?? data.totalPages ?? 1)),
  };
}

/* =========================
   SINGLE REQUEST DETAILS
========================= */
export async function fetchMaintenanceRequestById(id) {
  if (!id || id === 'undefined' || id === 'null') {
    throw new Error('Invalid maintenance request ID');
  }
  
  const res = await authFetch(`${MAINTENANCE_BASE}/request/${encodeURIComponent(id)}`, {
    credentials: "include",
    headers: await buildAuthHeaders()
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to load request details");

  return data.data || null;
}

/* =========================
   CREATE REQUEST (Office)
========================= */
export async function createMaintenanceRequest(body) {
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const auth = await buildAuthHeaders();
  const headers = {};
  if (auth.Authorization) headers.Authorization = auth.Authorization;
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const res = await authFetch(`${MAINTENANCE_BASE}/request`, {
    method: "POST",
    credentials: "include",
    headers,
    body: isFormData ? body : JSON.stringify(body),
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to create request");

  return data;
}

/* =========================
   UPDATE REQUEST (Admin)
========================= */
export async function updateMaintenanceRequest(id, body) {
  const res = await authFetch(`${MAINTENANCE_BASE}/request`, {
    method: "PUT",
    credentials: "include",
    headers: await buildAuthHeaders(),
    body: JSON.stringify({
      ...body,
      id: Number(id)
    })
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to update request");

  return data;
}

/* =========================
   UPDATE STATUS ONLY
========================= */
export async function updateMaintenanceStatus(body) {
  const res = await authFetch(`${MAINTENANCE_BASE}/request/status`, {
    method: "PUT",
    credentials: "include",
    headers: await buildAuthHeaders(),
    body: JSON.stringify(body)
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to update status");

  return data;
}

/* =========================
   ASSIGN STAFF / VENDOR
========================= */
export async function assignMaintenanceRequest(body) {
  const res = await authFetch(`${MAINTENANCE_BASE}/assign`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders(),
    body: JSON.stringify(body)
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to assign request");

  return data;
}

/* =========================
   DELETE / CANCEL REQUEST
========================= */
export async function deleteMaintenanceRequest(id) {
  const res = await authFetch(`${MAINTENANCE_BASE}/request/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
    headers: await buildAuthHeaders()
  });

  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to delete request");

  return data;
}